export default `# React Introduction

## What You Will Learn

To make the most of this course, you should know HTML, CSS, and JavaScript. And be familiar with the ES6 syntax.

This is a hands-on course. As part of it, you’ll build a web application, called "Sweet Pumpkins", using an external API — The Movie DB — to help you find the best movie to watch.

![Sweet Pumpkins](https://sweetpumpkins.codecamps.com/sw-ready.jpg)

Most of the code snippets are interactive. Which means you can experiment. If you want to go back to the original code,
click on the grey rounded-arrow icon above the code window.

Often, when introducing a new concept, I use simplifications, to make it easier to digest. Later, you will learn more advanced approach.

Let's get cracking!

## What is React?

React is a front-end JavaScript library for building interactive User Interfaces (UI). It was developed by Facebook a while ago
and open-sourced in 2013. In October 2017 it was re-licensed under the MIT license, meaning it's now free to use and modify.

React is used by big corporations, including Facebook, Instagram, Netflix, Dropbox and WhatsApp so you can rest assured it’s
been well tested and is here to stay. Which means that your time invested in learning React will pay off.

When you build the User Interface with React, everything on the website is a \`component\` — an element that encapsulates the look and the logic.
Components are like Lego blocks. They are re-usable.

The title, paragraph, button are components. So is the menu, header, and footer.

The simplest React component is the functional component. It is created using a regular JavaScript function syntax.

Let's look at an example (you can edit the code):

\`\`\`react
const Title = () => <h1>Sweet Pumpkins</h1>

render(
  <Title />
)
\`\`\`

Above, the \`Title\` is assigned an anonymous arrow function. The function returns an HTML-like element.

Notice that when an arrow function returns the result immediately, like in the example above, we don't have to use
the \`return\` keyword. When the elements returned by the
arrow function grow, it's a good practice to wrap them in parenthesis:

\`\`\`react
const Title = () => (
  <h1>Sweet Pumpkins</h1>
)

render(
  <Title />
)
\`\`\`

If you want to add some logic before returning the result, you need to also use the curly braces, like in any other JavaScript function:

\`\`\`react
const Title = () => {
  // some logic goes here
  return (
    <h1>Sweet Pumpkins</h1>
  )
}

render(
  <Title />
)
\`\`\`

Below the \`Title\` declaration, the \`render\` function displays the \`<Title />\`component on the page.

To tell React what you want to render, you will use the syntax called JSX (JavaScript XML). It's similar to HTML tags. But instead of HTML tags, it uses the
names of previously declared components. The component's name should be capitalised.

We will discuss the \`render\` function in detail in the following chapters. At the moment, let's assume that it's responsible for the rendering of a component.

In HTML, elements have an opening and closing tag like a header tag:

\`\`\`plainHtml
<h1>Sweet Pumpkins</h1>
\`\`\`

or just a single tag with attributes like an image tag:

\`\`\`plainHtml
<img src="URL_to_the_image" alt="description"/>
\`\`\`

React components use exactly the same structure when they want to get displayed. You can use either —
\`<Title />\` or \`<Title> Some content </Title>\` — depending on what you want to achieve.

## Props
Because the React's functional component is a function, it can receive arguments. The function that declares the component
receives one argument, called \`props\`.

The \`props\` is an object. And we can access its properties inside the component's declaration.

Let's add \`props\` to the \`Title\` component:

\`\`\`react
const Title = (props) => (
  <h1>Sweet Pumpkins</h1>
)

render(
  <Title />
)
\`\`\`

Above, the \`props\` object is passed as an argument to the \`Title\` component.

When you render the \`Title\` you can add an attribute to it in the same way as you add a \`src\` attribute to the \`img\` tag in HTML.
Every attribute passed to the \`Title\` becomes a property of the \`props\` object.

Let's see how it works:

\`\`\`react
const Title = (props) => (
  <h1>{props.title}</h1>
)

render(
  <Title title="Sweet Pumpkins"></Title>
)
\`\`\`

In the example above, the \`Title\` component receives a \`title\` attribute as the property. React adds it to the \`props\` object and you can access it in the \`Title\` declaration using \`props.title\`.

When you mix JavaScript in the component's declaration, you have to wrap it in curly braces. That's why instead of placing just a word \`props.title\` between the \`h1\` tags,
you have to wrap it in curly braces: \`<h1>{props.title}</h1>\`.

The \`props\` object looks like this:

\`\`\`plainJavascript
props = {
  ...,
  title: "Sweet Pumpkins"
}
\`\`\`

The \`...\` stands for other properties.

You can pass as many properties to the React component as you wish. They will be accessible in the component's declaration in the \`props\` object.

By default, React adds one more property to \`props\`: \`children\`. For the \`Title\` component, \`children\` is everything that you place between
opening and closing \`Title\` tags:

\`\`\`react
const Title = (props) => (
  <h1>{props.children}</h1>
)

render(
  <Title>Sweet Pumpkins</Title>
)
\`\`\`

Instead of passing "Sweet Pumpkins" as a \`title\` attribute, you can place it between an opening and closing \`Title\` tag. And then access and display it using
\`props.children\`.

Component's children may be as simple as the "Sweet Pumpkins" text or they may consist of other components.

Now, you are ready to start building the "Sweet Pumpkins" app. At first, you will build simple functional components. Later, some of them will grow.

Let's start with the \`MovieList\` component. It should take a list of movies and display it.

Below, the \`MovieList\` consists of an opening and closing \`div\` element and displays \`props.children\`. In the render method, the \`MovieList\` wraps a
list of movies:

\`\`\`react
const MovieList = (props) => (
  <div>
    {props.children}
  </div>
)

render(
  <MovieList>
    <ul>
      <li>Breaking bad</li>
      <li>Narcos</li>
      <li>Game of Thrones</li>
    </ul>
  </MovieList>
)
\`\`\`


Although it does the job this solution is suboptimal. Why? Because you had to create a list of movie titles manually, using \`li\` elements.

It would make more sense if you could place a list of movies in an array, iterate through it and generate a
list of \`li\` elements with a movie title inside.

Do you remember the ES6 \`map\` method? It does exactly what we need.

Let's have a look at the \`map\` method example below. The \`numbers\` array uses the \`map\` method. It takes an anonymous arrow function as an argument
and for every \`number\`
in a \`numbers\` array returns a result of the \`2 * number\` multiplication to create a new \`doubles\` array:

\`\`\`plainJavascript
  const numbers = [1,2,3,4,5];

  const doubles = numbers.map( number => 2 * number );
  // [2,4,6,8,10]
\`\`\`

What if we could iterate through an array of movie titles and return \`li\` elements instead?

Let's pass the \`titles\` array to the \`MovieList\`, iterate through it and return an array of \`<li>{title}</li>\` elements:

\`\`\`react
const titles = ["Breaking bad", "Narcos", "Game of Thrones"];

const MovieList = (props) => (
  <ul>
    {
      props.titles.map( title =>
        <li>{title}</li>
      )
    }
  </ul>
)

render(
  <MovieList titles={titles} />
)
\`\`\`

Because the \`MovieList\` uses the JavaScript logic to \`map\` through \`props.titles\`, we have to wrap the logic in curly braces.

The \`map\` method takes the \`titles\` array, iterates through it and for every \`title\` generates a \`li\` element with a respective title inside.

If you inspected the \`props\` object it would look like this:

\`\`\`plainJavascript
props = {
  ...,
  titles: ["Breaking bad", "Narcos", "Game of Thrones"]
}
\`\`\`

Once the \`map\` method returns an array of \`li\` elements, React will place them inside of unordered list tags (\`ul\`):

\`\`\`plainHtml
<ul>
  <li>Breaking bad</li>
  <li>Narcos</li>
  <li>Game of Thrones</li>
</ul>
\`\`\`

When you inspect the results in the Developers Tools you will see the expected structure:

![React movie list](https://s3.eu-west-2.amazonaws.com/sweetpumpkins/react/first-movie-list-min.jpg)

You've just created the first list of elements with React.

Later, you will learn how to manipulate a list of elements.

For example, you could remove a movie from or add a new movie to the list.

To keep an eye on the dynamically changing list of elements, React requires that we add a unique identifier to every
item on the list. It's called a \`key\` attribute. The \`key\` attribute should be unique and it should be a string.

Let's comply with this requirement. We can either use the movie title itself as a \`key\` (as long as it's unique) or we can use the second argument of the \`map\` function.

The second argument of the \`map\` method is an \`index\` that changes on every iteration through an array.
An \`index\` is a number so we have to convert it to a string, using the \`toString\` method:

\`\`\`react
const titles = ["Breaking bad", "Narcos", "Game of Thrones"];

const MovieList = (props) => (
  <ul>
    {
      props.titles.map( (title, index) => (
        <li key={index.toString()}>{title}</li>
      ))
    }
  </ul>
)

render(
  <MovieList titles={titles} />
)
\`\`\`

If you open Developers Tools, you will see that nothing has changed. The list looks exactly the same as before. There are no keys attached to \`li\` elements!

So, where does React put them?

## Virtual DOM

React was built to create high-performance applications. But DOM manipulations are slow. That's why React creates an additional DOM-like structure that is kept in the memory.
It's called the Virtual DOM.

When you change, add or remove components, React first applies the changes to the Virtual DOM and then compares them with an actual DOM.
In the end, it only applies the changes that are necessary to keep both DOM and Virtual DOM in sync. This process is called reconciliation.

The good news is that you can inspect the Virtual DOM.
The React team built [React Chrome Developer Tools](https://chrome.google.com/webstore/detail/react-developer-tools/fmkadmapgofadopljbjfkapdkoienihi).
You can find them also for [Mozilla Firefox](https://addons.mozilla.org/en-GB/firefox/addon/react-devtools/).

Once you install the extension (you may have to re-launch the browser) and open the Developer Tools window you should see the "React" tab at the top. If you don't see it you can either increase the size of the Developer Tools window or click
the arrows \`>>\` in the tabs panel to display more tabs and than grab the "React" tab and place it next to "Elements" and "Console" for easy access.

Now, you can inspect the React components. In the left-hand side panel, you will see the React Virtual DOM and in the right-hand side panel the \`props\` object.

You can inspect which \`props\` have been passed to the \`MovieList\`. And you can also recognise the \`key\`s attached to \`li\` elements in the Virtual DOM.

![React Develeper Tools](https://sweetpumpkins.codecamps.com/r-introducion-001.jpg)

At the moment our components are small. Later, they will grow.

It's good practice to extract even small components that encapsulate a certain logic into separate components.

To prepare for future challenges, let's create a \`MovieListItem\` component:

\`\`\`react
const titles = ["Breaking bad", "Narcos", "Game of Thrones"];

const MovieListItem = (props) => (
  <li>{props.title}</li>
)

const MovieList = (props) => (
  <ul>
    {
      props.titles.map( (title, index) =>
        <MovieListItem key={index.toString()} title={title} />
      )
    }
  </ul>
)

render(
  <MovieList titles={titles} />
)
\`\`\`

The \`MovieListItem\` is a tiny component that has only one job to do – display the movie title.

The \`MovieList\` passes the title in the \`title\` attribute. Then, the \`MovieListItem\` can access it through \`props\` and render it between \`li\` tags.

At the moment the \`MovieListItem\` accesses the \`title\` using the \`props.title\`. We can simplify the code and improve its readability using the ES6 destructuring.

ES6 destructuring lets you extract properties from an object in an easy way. Imagine that you have a movie object:

\`\`\`plainJavascript
const movie = {
  title: "Breaking bad",
  releaseYear: 2008,
  isFavouriteMovie: true

}
\`\`\`

If you want to get just the \`movieTitle\` and the \`releaseYear\` you could write:
\`\`\`plainJavascript
const title = movie.title;
const releaseYear = movie.releaseYear;
\`\`\`

Or you could use ES6 destructuring syntax:

\`\`\`plainJavascript
const { title, releaseYear } = movie;
\`\`\`

You know that both the \`MovieList\` and the \`MovieListItem\` receive a \`props\` object.

Now, using the curly braces you can destructure the \`props\` object on the fly
and get only the properties that you want to use.

Instead of the \`props.title\`, you can use the \`title\` in the \`MovieListItem\` and instead of the \`props.titles\` you can use the \`titles\` in the \`MovieList\`.

Below, the \`props\` of both the \`MovieListItem\` and the \`MovieList\` are destructured:

\`\`\`react
const titles = ["Breaking bad", "Narcos", "Game of Thrones"];

const MovieListItem = ({ title }) => (
  <li>{title}</li>
)

const MovieList = ({ titles }) => (
  <ul>
    {
      titles.map( (title, index) => (
        <MovieListItem key={index.toString()} title={title} />
      ))
    }
  </ul>
)

render(
  <MovieList titles={titles} />
)
\`\`\`


## Summary

In this chapter, you've learned how to use React's functional components.
You've also discovered how to iterate through an array of components and how to pass the \`props\` between them.

In the next chapter, you will dive into class components and find out how React applications are structured.


`;



// WEBPACK FOOTER //
// ./src/book/chapters/react-introduction.js